import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StepShell from "../components/StepShell";
import Field from "../components/Field";
import Input from "../components/Input";
import PrimaryButton from "../components/PrimaryButton";
import { useForm } from "../context/FormContext";
import useEnterToContinue from "../hooks/useEnterToContinue";

const TOTAL = 6;

export default function StepName() {
  const { formData, setFormData } = useForm();
  const navigate = useNavigate();
  const [touched, setTouched] = useState(false);

  const error = useMemo(() => {
    if (!touched) return "";
    if (!formData.name.trim()) return "Please enter your name.";
    if (formData.name.trim().length < 2) return "Name looks too short.";
    return "";
  }, [formData.name, touched]);

  const canContinue = !error && formData.name.trim().length >= 2;

  useEnterToContinue(() => navigate("/dob"), canContinue);

  return (
    <StepShell step={1} totalSteps={TOTAL} title="Your next chapter" showBack={false}>
      {/* The first screen is where most starters leave, so it shows what they
          get and what to have ready before asking for anything. */}
      <div className="w-full max-w-md mx-auto overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg">
        <div className="relative h-44">
          <picture>
            <source srcSet="/images/reader-phone.webp" type="image/webp" />
            <img src="/images/reader-phone.jpg" alt="" decoding="async"
              className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: "50% 35%" }} />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 text-white">
            <p className="text-lg font-semibold leading-tight">Your timeline, in about 2 minutes</p>
            <ul className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-medium">
              {["Free", "No account", "Ready on screen"].map((fact) => (
                <li key={fact} className="rounded-full bg-white/90 px-2 py-0.5 text-gray-800">✓ {fact}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="space-y-5 p-6 sm:p-7">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">What should we call you?</h2>
            <p className="text-gray-600 mt-1">Your first name is enough.</p>
          </div>

          <Field label="Name" error={error}>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              onBlur={() => setTouched(true)}
              placeholder="Your first name"
              aria-label="First name"
              autoComplete="given-name"
              autoFocus
            />
          </Field>

          <PrimaryButton
            disabled={!canContinue}
            onClick={() => navigate("/dob")}
          >
            Next
          </PrimaryButton>

          <div className="rounded-2xl bg-orange-50 p-3 text-xs text-gray-700">
            <p className="font-semibold text-gray-900">Have these ready</p>
            <p className="mt-1">
              📅 Birth date · 🕐 Birth time (a kendara or birth certificate helps) · 📍 Birthplace
            </p>
          </div>
        </div>
      </div>
    </StepShell>
  );
}
